import React, {memo, useCallback, useEffect, useState} from 'react';
import {Col, FormGroup, Label, Row} from "reactstrap";
import Select, {ValueType} from "react-select";
import {useDispatch, useSelector} from "react-redux";
import {useFormContext} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCity,
  faCompass,
  faHome,
  faLocationArrow,
  faRoad,
} from "@fortawesome/free-solid-svg-icons";

import Address from "../../../../Models/Address";
import {FormType} from "../../../../Models/FormType";
import {RootState} from "../../../../Store/Reducers";
import {ZoneActions} from "../../../../Store/Zone/Slice";
import {CityActions} from "../../../../Store/City/Slice";
import Zone from "../../../../Models/Zone";
import City from "../../../../Models/City";
import InputComponent from "../../Inputs/InputComponent";
import SelectAddonComponent, {AddonComponent, SelectContainerComponent} from "../../SelectAddonComponent";

export interface AddressFormProps {
  citySelectedP?: string;
  zoneSelectedP?: string;
  address?: Address;
  not_allowed_field?: boolean;
  formType: FormType;
}

const AddressFormComponent = memo((
  {
    citySelectedP,
    zoneSelectedP,
    address,
    not_allowed_field = false,
    formType
  }: AddressFormProps) => {

  const { register } = useFormContext();

  const isReadOnly = formType === FormType.SHOW;
  const {CityReducer: {cities}} = useSelector((state: RootState) => state);
  const zones = useSelector((state: RootState) => state.ZoneReducer.zonesByCity);

  const [zoneSelected, setZoneSelected] = useState<ValueType<any> | undefined>(undefined);
  const [citySelected, setCitySelected] = useState<ValueType<any> | undefined>(undefined);
  const [citiesOptions, setCitiesOptions] = useState<ValueType<any>[]>([])
  const [zonesOptions, setZonesOptions] = useState<ValueType<any>[]>([])

  const dispatch = useDispatch();

  const onSelectCity = useCallback(({label, value}: any) => {
    setCitySelected({label, value});
    setZoneSelected(undefined);
    dispatch(ZoneActions.getZonesByCity(value));
  }, [dispatch]);

  useEffect( () => {
    !cities.length && dispatch(CityActions.getCities({}));
    if (citySelectedP) {
      onSelectCity({ value: citySelectedP });
      if (zoneSelectedP) {
        const zone = zones.find((z: Zone) => z._KEY === zoneSelectedP) as Zone;
        if (zone) setZoneSelected({ value: zoneSelectedP, label: zone.name })
      }
    }
  }, [dispatch, citySelectedP, zoneSelectedP, onSelectCity, setZoneSelected, setCitySelected, zones, cities]);

  useEffect(() => {
    if ( address?.city && !citySelected) {
      const city = address.city as City;
      setCitySelected({value: city._KEY, label: city.name });
      dispatch(ZoneActions.getZonesByCity((address.city as City)._KEY));
    }
  }, [dispatch, cities, address, citySelected])

  useEffect(() => {
    if ( address && !zoneSelected) {
      if ( typeof address.zone === "string" ) {
        const zone = zones.find((z: Zone) => z._KEY === address.zone) as Zone;
        if (zone) setZoneSelected({ label: zone.name, value: address.zone });
      } else if (address.zone) {
        const zone =  (address.zone as Zone);
        setZoneSelected({ label: zone.name, value: zone._KEY});
      }
    }
  }, [dispatch, zones, address, zoneSelected])

  useEffect(() => {
    if (!!cities.length) {
      setCitiesOptions(cities.map(c => ({ value: c._KEY, label: c.name })))
    }
  }, [cities, setCitiesOptions])

  useEffect(() => {
    if (!!zones.length && !!cities.length) {
      setZonesOptions(zones.map((z: Zone) => ({ value: z._KEY, label: z.name})))
    } else {
      setZonesOptions([]);
    }
  }, [cities, zones, setZonesOptions])

  return (
    <>
      <Row>

        <Col lg="4" md="6" sm="12">
          <InputComponent
            label="CIUDAD"
            name="address_city"
            required={!isReadOnly}
            render={({onChange, value}) =>(
              <SelectAddonComponent>
                <AddonComponent>
                  <FontAwesomeIcon icon={faCity}/>
                </AddonComponent>
                <SelectContainerComponent>
                  <Select
                    options={citiesOptions}
                    onChange={(changed: ValueType<any>) => {
                      onSelectCity(changed)
                      onChange(changed)
                    }}
                    value={citySelected}
                    isLoading={!cities.length}
                    isDisabled={isReadOnly}
                  />
                </SelectContainerComponent>
              </SelectAddonComponent>
            )}
          />
        </Col>

        <Col lg="4" md="6" sm="12">
          <InputComponent
            label="ZONA"
            name="address_zone"
            render={({onChange, value}) => {
              return(
                <SelectAddonComponent>
                  <AddonComponent>
                    <FontAwesomeIcon icon={faCompass}/>
                  </AddonComponent>
                  <SelectContainerComponent>
                    <Select
                      options={zonesOptions}
                      onChange={(changed: ValueType<any>) => {
                        setZoneSelected(changed);
                        onChange(changed);
                        setZoneSelected(undefined);
                      }}
                      value={zoneSelected}
                      isLoading={zonesOptions.length === undefined}
                      isDisabled={isReadOnly}
                    />
                  </SelectContainerComponent>
                </SelectAddonComponent>
            )}}
          />
        </Col>

        <Col lg="4" md="6" sm="12">
          <InputComponent
            label="BARRIO"
            name="address_neighborhood"
            icon={faRoad}
            defaultValue={address?.neighborhood}
            disabled={isReadOnly}
          />
        </Col>

        <Col lg="4" md="6" sm="12">
          <InputComponent
            label="DIRECCIÓN"
            name="address_address"
            icon={faLocationArrow}
            defaultValue={address?.address}
            required={!isReadOnly}
            disabled={isReadOnly}
          />
        </Col>
        <Col lg="4" md="6" sm="12">
          <InputComponent
            label="EDIFICIO"
            name="address_building"
            icon={faBuilding}
            defaultValue={address?.building}
            disabled={isReadOnly}
          />
        </Col>

        <Col md="6" lg="4" sm="12">
          <InputComponent
            label="INTERIOR"
            name="address_interior"
            icon={faHome}
            defaultValue={address?.interior}
            disabled={isReadOnly}
          />
        </Col>

        {
          not_allowed_field &&
          <Col md="3">
            <FormGroup>
              <Label>NO PERMITIDO</Label>
              <div className="checkbox text-center">
                <input
                  name="address_notallow"
                  type="checkbox"
                  className="form-checkbox"
                  ref={register}
                  defaultChecked={address?.notallow}
                  disabled={isReadOnly}
                />
              </div>
            </FormGroup>
          </Col>
        }
      </Row>
    </>
  );
});

export default AddressFormComponent;
